import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { Control, FieldErrors, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dimensions, Platform, StyleSheet, View } from 'react-native'
import { Button, TextInput } from 'react-native-paper'
import {
  ContributionStatus,
  ContributionType,
  SubmitContributionInput,
  useSubmitContributionMutation,
  useUpdateContributionStatusMutation,
} from '../../../../../generated/graphql'
import { useTheme } from '../../../../../hooks/theme.hook'
import { Contribution } from '../types'
import { useValidationMessage } from '../components/validation-message'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../../utils/message-type'
import * as Sentry from '@sentry/react-native'
import { useHeaderButton } from '../../../../../contexts/header-button.provider'
import Row from '../../../../../@ui/containers/grid/row'
import { parseSpacesBetweenTags } from '../../../../../utils/html.utils'
import { ControlledTextInput } from '../../../../../@ui'
import { HTMLErrorBox } from './html-error.box'
import { ApolloError } from '@apollo/client'
import {
  ERROR_CODES,
  hasApolloErrorCode,
} from '../../../../../utils/error-utils'

const ErrorFloatingBox = ({
  htmlValidationError,
  onClose,
}: {
  htmlValidationError: string[]
  onClose: () => void
}) => {
  const { t } = useTranslation('contributions')
  const theme = useTheme()
  const [height, setHeight] = React.useState(0)
  return (
    <View
      onLayout={(event) => {
        !height && setHeight(event.nativeEvent.layout.height) // LayoutRectangle has no top, but the event result has top information
      }}
      style={{
        zIndex: 100,
        position: 'absolute',
        bottom: Dimensions.get('window').height - 64 - height,
        left: 0,
        right: 0,
        backgroundColor: theme.colors.editBkg,
        borderRadius: theme.spacing(5),
      }}
    >
      <HTMLErrorBox
        title={t('html-validation-error')}
        html={htmlValidationError}
        onClose={onClose}
      />
    </View>
  )
}

const contributionTypeHeights: Record<ContributionType, number> = {
  [ContributionType.Reserved]: 100,
  [ContributionType.Edit]: 280,
  [ContributionType.OwnerEdit]: 280,
  [ContributionType.Challenge]: 280,
  [ContributionType.Inquiry]: 150,
} as const
// New component outside the main function
const ContributionInputs = ({
  context,
  contribution,
  control,
  disabled,
  errors,
}: {
  context: string
  contribution: Contribution
  control: Control<FormShape>
  disabled?: boolean
  errors: FieldErrors<FormShape>
}) => {
  const { t } = useTranslation('contributions')
  const { intendedType } = contribution
  const theme = useTheme()
  const input = useWatch({
    control,
    name: 'input',
  })

  if (
    [ContributionType.Edit, ContributionType.OwnerEdit].includes(
      intendedType as ContributionType
    )
  ) {
    return (
      <>
        <ControlledTextInput
          control={control}
          name={'input'}
          disabled={disabled}
          placeholder={t('Type your text')}
          rules={{
            validate: (value) => value !== contribution.originalText,
          }}
          label={t('Proposed Text')}
          error={!!errors.input}
          mode="outlined"
          autoCapitalize="none"
          helperText={errors.input?.message || t('Type your text')}
          multiline={true}
        />
        <ControlledTextInput
          control={control}
          name={'motivation'}
          disabled={disabled}
          placeholder={t('Type your justification')}
          label={t('Justification')}
          mode="outlined"
          autoCapitalize="none"
          helperText={t('Type your justification')}
          multiline={true}
        />
      </>
    )
  }

  if (intendedType === ContributionType.Challenge) {
    return (
      <>
        <ControlledTextInput
          control={control}
          name={'input'}
          disabled={disabled}
          placeholder={t('Type your challenge')}
          rules={{
            required: t('requiredChallenge'),
            validate: (value) => !!value.trim() || t('requiredChallenge'),
          }}
          label={t('Challenge')}
          error={!!errors.input}
          mode="outlined"
          autoCapitalize="none"
          helperText={errors.input?.message || t('Type your challenge')}
          multiline={true}
        />
        <ControlledTextInput
          control={control}
          name={'motivation'}
          disabled={disabled}
          placeholder={t('Provide a reference')}
          rules={{
            required: t('requiredReference'),
            validate: (value) => !!value.trim() || t('requiredReference'),
          }}
          label={t('Reference')}
          mode="outlined"
          error={!!errors.motivation}
          autoCapitalize="none"
          helperText={errors.motivation?.message || t('Provide a reference')}
          multiline={true}
        />
      </>
    )
  }

  if (intendedType === ContributionType.Inquiry) {
    return (
      <>
        {/* This is a hack to make the text not scroll too high on Android */}
        <TextInput
          style={{
            position: 'absolute',
            zIndex: -1,
            height: 1,
            width: 1,
            top: 20,
            left: 20,
          }}
          disabled={true}
        />
        <ControlledTextInput
          control={control}
          name={'input'}
          disabled={disabled}
          placeholder={t('Type your inquiry')}
          rules={{
            required: t('requiredInquiry'),
            validate: (value) => !!value.trim() || t('requiredInquiry'),
          }}
          label={t('Inquiry')}
          error={!!errors.input}
          mode="outlined"
          autoCapitalize="none"
          helperText={errors.input?.message || t('Type your inquiry')}
        />
      </>
    )
  }

  return null
}

interface ContributionInputProps {
  contribution: Contribution | null
  onLayoutOffset: (height: number) => void
  onClose: () => void | Promise<void>
  context: string
}

interface FormShape {
  input: string
  motivation: string
}

export function ContributionInput(props: ContributionInputProps) {
  const { contribution, context, onLayoutOffset } = props
  /**
   * Basic Hooks and State
   */
  const theme = useTheme()
  const { t } = useTranslation('contributions')
  const style = useStyle()

  const [submitDisabled, setSubmitDisabled] = React.useState(false)
  const [isConfirming, setIsConfirming] = React.useState(false)

  /**
   * Api Actions
   */
  const [doSubmitContribution, { loading: saving }] =
    useSubmitContributionMutation()
  const [doUpdateContributionStatus] = useUpdateContributionStatusMutation()

  /**
   * Form State
   */
  const { control, watch, formState, setValue } = useForm<FormShape>({
    defaultValues: {
      input: '',
      motivation: '',
    },
    mode: 'onTouched',
  })

  const { errors, isValid } = formState
  const [input, motivation] = watch(['input', 'motivation'])

  // Check for warnings in edit contributions
  const htmlValidationError = useValidationMessage(
    input,
    contribution || ({ originalText: '', begin: 0 } as Contribution),
    context
  )
  const hasWarnings = React.useMemo(() => {
    if (
      !contribution ||
      ![ContributionType.Edit, ContributionType.OwnerEdit].includes(
        contribution.intendedType as ContributionType
      )
    ) {
      return false
    }

    return htmlValidationError.length > 0
  }, [htmlValidationError, contribution])

  useEffect(() => {
    setSubmitDisabled(!isValid)
  }, [isValid])
  /**
   * Memos
   */
  const backgroundColor = useMemo(() => {
    if (!contribution) {
      return undefined
    }
    const contributionTypeColors: Record<ContributionType, string> = {
      [ContributionType.Reserved]: theme.colors.editBkg,
      [ContributionType.Edit]: theme.colors.editBkg,
      [ContributionType.OwnerEdit]: theme.colors.editBkg,
      [ContributionType.Challenge]: theme.colors.challengeBkg,
      [ContributionType.Inquiry]: theme.colors.inquiryBkg,
    } as const

    if (!contribution.intendedType) {
      return undefined
    }
    return contributionTypeColors[contribution.intendedType] || undefined
  }, [theme, contribution?.intendedType])

  /**
   * Cancel and submit actions are on the header.
   * When a contribution is selected or removed we update this header
   * and the callback functions associated with them
   */
  const { setHandler: setSaveHandler, setMeta } =
    useHeaderButton<Contribution>('saveContribution')
  const { setHandler: setCancelHandler, setDisabled: setCancelDisabled } =
    useHeaderButton<Contribution>('cancelContribution')

  const updateReservedMetaData = React.useCallback(
    (contribution: Contribution | null) => {
      if (!contribution) {
        setSaveHandler(null)
        setCancelHandler(null)
        setMeta(null)
        return
      }
      const cancelContribution = async () => {
        const input = {
          contributionId: contribution.id,
          status: ContributionStatus.Cancelled,
        }

        try {
          setCancelDisabled(true)
          await doUpdateContributionStatus({ variables: { input } })
        } catch (e) {
          Toast.show((e as Error).message, MessageType.error)
          console.error(e)
          Sentry.captureException(e)
        } finally {
          setSaveHandler(null)
          setCancelHandler(null)
          setMeta(null)
          props.onClose()
        }
      }

      setMeta(contribution)
      setSaveHandler(() => {
        //
      })
      setCancelHandler(cancelContribution)
      setCancelDisabled(!contribution)
    },
    [contribution]
  )

  useEffect(() => {
    updateReservedMetaData(contribution)
    // Reset the form when the modal shows or hide
    setValue('input', '')
    setValue('motivation', '')
    setIsConfirming(false) // Reset confirmation state
    onLayoutOffset(
      contributionTypeHeights[contribution?.intendedType as ContributionType]
    )
    if (contribution) {
      // When is Edit or owner we initialize the input
      if (
        [ContributionType.Edit, ContributionType.OwnerEdit].includes(
          contribution?.intendedType as ContributionType
        )
      ) {
        setValue('input', contribution?.originalText)
      }
    }
  }, [contribution?.id])

  /**
   * Submit Action
   */
  const onSubmitContribution = React.useCallback(
    async (
      input: string,
      motivation: string,
      reservedContribution: Contribution
    ) => {
      setSubmitDisabled(true)

      // Normalize HTML to remove spaces between tags for consistent offset calculations
      const normalizedInput =
        reservedContribution.intendedType == ContributionType.Edit
          ? parseSpacesBetweenTags(input)
          : input.trim()

      const updateInput: SubmitContributionInput = {
        contributionId: reservedContribution.id,
        input: normalizedInput,
        motivation: motivation.trim(),
        type: reservedContribution.intendedType!,
      }

      try {
        await doSubmitContribution({
          variables: { input: updateInput },
        })

        props.onClose()

        Toast.show(t('The contribution has been made'), MessageType.info)
      } catch (e) {
        if (
          e instanceof ApolloError &&
          hasApolloErrorCode(e, ERROR_CODES.PAYMENT_REQUIRED)
        ) {
          Toast.show(
            t('errors:node-active-user-limit-reached'),
            MessageType.error
          )
          return
        }
        Toast.show((e as Error).message, MessageType.error)
        console.error(e)
        Sentry.captureException(e)
      }

      setSubmitDisabled(false)
    },
    [contribution, props.onClose]
  )

  /**
   * Handle submit button press - check for warnings first
   */
  const handleSubmitPress = React.useCallback(() => {
    if (!contribution) return

    // If warnings make double check
    if (hasWarnings && !isConfirming) {
      setIsConfirming(true)
      return
    }
    onSubmitContribution(input, motivation, contribution)
    setIsConfirming(false)
  }, [
    hasWarnings,
    isConfirming,
    input,
    motivation,
    contribution,
    onSubmitContribution,
  ])

  if (!contribution) {
    return null
  }
  return (
    <View style={[style.container, { backgroundColor }]}>
      {control._getDirty('input') && isConfirming && (
        <ErrorFloatingBox
          htmlValidationError={htmlValidationError}
          onClose={() => setIsConfirming(false)}
        />
      )}
      <ContributionInputs
        control={control}
        errors={errors}
        context={context}
        contribution={contribution}
        disabled={saving}
      />
      <Row alignItems={'center'} justifyContent={'flex-end'}>
        <Button
          disabled={submitDisabled}
          mode={'contained'}
          buttonColor={isConfirming ? theme.colors.error : undefined}
          onPress={handleSubmitPress}
        >
          {isConfirming ? t('Confirm') : t('Submit')}
        </Button>
      </Row>
    </View>
  )
}

const useStyle = createStyle(({ theme }) => ({
  container: {
    paddingVertical: theme.spacing(5),
    marginTop: -theme.spacing(5),
    paddingHorizontal: theme.spacing(2),
    borderTopLeftRadius: theme.spacing(5),
    borderTopRightRadius: theme.spacing(5),
    borderTopWidth: 1,
    borderTopColor: 'white',
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
}))
